import { useEffect, useRef, useState } from "react"
import Api from "../api/api"
import { Organization, Product } from "../api/models"
import { requiredMark } from "./InstallerFields"
import { ImageFieldView, useImageField } from "./ImageField"
import { LOGO_FIELD_HINT, useProductLogoField } from "./LogoPlacementDialog"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// shared by Add/EditProductDialog's numeric fields
export const optionalNonNegativeInt = (text: string) =>
  text === "" || (Number.isInteger(Number(text)) && Number(text) >= 0)
export const optionalDollarAmount = (text: string) =>
  text === "" || (/^\d+(\.\d{1,2})?$/.test(text.trim()))

// Create a brand-new product. A new product has no AL (AI assistant) config
// — AL wiring stays SQL-managed for now — and starts outside every sku (a
// sku version's claims pull it in). Trial defaults mirror the backend's
// (14 days / 2 activations); clearing a trial field means "no trial".
// Images are keyed by the product's id, so saving is create → upload →
// patch: if an upload fails the product already exists — the dialog says
// so, locks the other fields, and offers a retry of the images only
// (closing hands the created product to onCreated all the same).
// Conditionally mounted (OfferingDialog pattern).
export default function AddProductDialog({ accessToken, isAdmin, onClose, onCreated }: {
  accessToken: string
  isAdmin: boolean
  onClose: () => void
  onCreated: (product: Product) => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  const [name, setName] = useState("")
  const [url, setUrl] = useState("")
  const [value, setValue] = useState("")
  const [needsCredits, setNeedsCredits] = useState(false)
  const [trialDays, setTrialDays] = useState("14")
  const [trialMaxActivations, setTrialMaxActivations] = useState("2")
  // system admin only: which ORGANIZATION owns the product ("" = the
  // backend's default, the creator's own org); members always create in
  // their org
  const [owner, setOwner] = useState("")
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [step, setStep] = useState<"form" | "saving" | "error">("form")
  const [errorMessage, setErrorMessage] = useState("")
  // the product once it exists; the ref serves the dialog's close handler,
  // which runs before a state update would reach it
  const [created, setCreated] = useState<Product | null>(null)
  const createdRef = useRef<Product | null>(null)
  const screenshot = useImageField(accessToken, (width, productId) =>
    Api.createProductImageUpload({ productId, width, accessToken }))
  const { logo, editor: logoEditor, adjust: adjustLogo } = useProductLogoField({ accessToken, ownerId: owner })

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  useEffect(() => {
    if (!isAdmin || !accessToken) return
    let stale = false
    Api.listOrganizations({ accessToken })
      .then(({ organizations }) => { if (!stale) setOrganizations(organizations) })
      .catch(() => { })
    return () => { stale = true }
  }, [isAdmin, accessToken])

  const formValid =
    name.trim() !== ""
    && name.trim().length <= 64
    && (url.trim() === "" || url.trim().startsWith("https://"))
    && optionalDollarAmount(value)
    && optionalNonNegativeInt(trialDays)
    && optionalNonNegativeInt(trialMaxActivations)

  const handleSubmit = async () => {
    if (!formValid) return
    setStep("saving")
    setErrorMessage("")
    try {
      if (createdRef.current === null) {
        const { product } = await Api.createProduct({
          product: {
            name: name.trim(),
            ownerId: owner || undefined,
            url: url.trim() || undefined,
            valueCents: value.trim() !== "" ? Math.round(Number(value) * 100) : undefined,
            needsCredits,
            // the fields are prefilled with the defaults, so an empty field is
            // a deliberate clear — send null ("no trial"), never absent
            trialDays: trialDays.trim() !== "" ? Number(trialDays) : null,
            trialMaxActivations: trialMaxActivations.trim() !== "" ? Number(trialMaxActivations) : null
          },
          accessToken
        })
        createdRef.current = product
        setCreated(product)
      }
      const productId = createdRef.current.id
      const patch: Parameters<typeof Api.updateProduct>[0]["patch"] = {}
      if (screenshot.file) {
        const { width, rev } = await screenshot.upload(productId)
        patch.imageWidth = width
        patch.imageRev = rev
      }
      if (logo.file) {
        const { width, rev } = await logo.upload(productId)
        patch.logoWidth = width
        patch.logoRev = rev
      }
      if (Object.keys(patch).length > 0)
        createdRef.current = (await Api.updateProduct({ productId, patch, accessToken })).product
      // closing reports the product (handleClose)
      dialogRef.current?.close()
    } catch (err) {
      const message = err instanceof Error ? err.message : "unknown error"
      setErrorMessage(createdRef.current !== null
        ? `The product was created, but adding its images failed: ${message} — retry, or close and add them from Edit.`
        : message)
      setStep("error")
    }
  }

  const handleClose = () => {
    if (createdRef.current !== null) onCreated(createdRef.current)
    onClose()
  }

  const busy = step === "saving"
  // once the product exists only its images are still open
  const locked = busy || created !== null

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={handleClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content no-scrollbar" style={{ maxHeight: "85vh", overflowY: "auto" }}>
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Add Product</h2>
        <p className="generate-codes-subtitle">
          The plug-in itself — it joins a SKU once a version claims it, and
          AI-assistant support is wired up separately.
        </p>

        <div className="generate-codes-form">
          <label className="generate-codes-field">
            <span className="generate-codes-label">Name{requiredMark}</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="generate-codes-input"
              disabled={locked}
              maxLength={64}
              autoFocus
            />
          </label>

          <label className="generate-codes-field">
            <span className="generate-codes-label">Product page URL</span>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="generate-codes-input"
              disabled={locked}
              placeholder="https://…"
            />
            <span style={{
              fontSize: "9pt",
              minHeight: "1.3em",
              color: url.trim() !== "" && !url.trim().startsWith("https://") ? "#e07470" : "var(--nfa-light-grey)"
            }}>
              {url.trim() !== "" && !url.trim().startsWith("https://")
                ? "The URL must start with https:// — or be empty."
                : "Optional — the public product page link."}
            </span>
          </label>

          <label className="generate-codes-field">
            <span className="generate-codes-label">Value ($)</span>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="generate-codes-input"
              disabled={locked}
              placeholder="e.g. 99 — empty: no standalone value"
            />
            {!optionalDollarAmount(value) && (
              <span style={{ fontSize: "9pt", color: "#e07470" }}>
                Dollars with up to two decimals — or empty.
              </span>
            )}
          </label>

          <div style={{ display: "flex", gap: 16 }}>
            <label className="generate-codes-field" style={{ flex: 1 }}>
              <span className="generate-codes-label">Trial days</span>
              <input
                type="text"
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
                className="generate-codes-input"
                disabled={locked}
                placeholder="empty: no trial"
              />
            </label>
            <label className="generate-codes-field" style={{ flex: 1 }}>
              <span className="generate-codes-label">Trial activations</span>
              <input
                type="text"
                value={trialMaxActivations}
                onChange={(e) => setTrialMaxActivations(e.target.value)}
                className="generate-codes-input"
                disabled={locked}
              />
            </label>
          </div>
          {(!optionalNonNegativeInt(trialDays) || !optionalNonNegativeInt(trialMaxActivations)) && (
            <span style={{ fontSize: "9pt", color: "#e07470" }}>
              Trial fields must be non-negative whole numbers — or empty.
            </span>
          )}

          <label className="generate-codes-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={needsCredits}
              onChange={(e) => setNeedsCredits(e.target.checked)}
              disabled={locked}
            />
            <span className="generate-codes-label" style={{ margin: 0 }}>
              Pay-as-you-go (runs on credits)
            </span>
          </label>

          {isAdmin && (
            <div className="generate-codes-field">
              <span className="generate-codes-label">Owner organization</span>
              <select
                className="generate-codes-input"
                style={{ width: "auto" }}
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                disabled={locked}
              >
                <option value="">your organization</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
          )}

          <ImageFieldView fields={screenshot} busy={busy} />
          <ImageFieldView
            fields={logo}
            busy={busy}
            label="Logo (PNG, optional)"
            hint={LOGO_FIELD_HINT}
            variant="logo"
            onAdjust={adjustLogo}
          />
        </div>

        {step === "error" && errorMessage && (
          <p className="generate-codes-error">{errorMessage}</p>
        )}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={busy}>{created !== null ? "Close" : "Cancel"}</button>
          <button
            className="btn-upgrade"
            disabled={!formValid || busy}
            onClick={handleSubmit}
          >
            {busy ? <><Spinner size={12} /> {created !== null ? "Uploading…" : "Creating…"}</> : created !== null ? "Retry Images" : "Create Product"}
          </button>
        </div>
      </div>
      {logoEditor}
    </dialog>
  )
}
